"use client"

import { useForm, Controller } from "react-hook-form"
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Radio,
  RadioGroup,
  Stack,
  VStack,
  HStack,
  Heading,
  Tag,
  TagLabel,
  TagCloseButton,
  Container,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Text,
  Tooltip,
  Divider,
  FormErrorMessage,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  UserIcon,
  PhoneIcon,
  CalendarIcon,
  InformationCircleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline"
import { useEffect, useState, useMemo } from "react"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"

import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchCoreEducationAsync,
  fetchCoreSkillsAsync,
  selectCoreEducations,
  selectCoreSkills,
  fetchSuggestedSkillsAsync,
  selectSelectedSkills,
} from "../Employers Slice/employersJobCreateSlice/createJobSlice"
import {
  fetchPublicCoreLocationsAsync,
  selectPublicCoreLocations,
} from "../../jobSearch/FetchLocationSlice/FetchLocationSlice"

// Helpers
const getId = (obj) => (obj?.uuid ? obj.uuid : obj?.id)
const getTitle = (obj) => obj?.title || ""

// Yup schema
const JobDetails2Schema = yup.object({
  upload_cv: yup.boolean().required(),
  job_apply_age: yup.string().required("Age range is required"),
  candidate_gender: yup.string().required("Gender is required"),
  interview_type: yup.string().oneOf(["In-Person", "Online"]).required("Interview type is required"),
  candidate_contact_via: yup.string().required("Contact preference is required"),
  job_deadline: yup
    .string()
    .required("Application deadline is required")
    .test("future", "Deadline must be today or in the future", (value) => {
      if (!value) return false
      const selected = new Date(value)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return selected >= today
    }),
  min_job_exp: yup
    .string()
    .required("Minimum experience is required")
    .matches(/^\d+$/, "Please select a valid number"),
  max_job_exp: yup
    .string()
    .required("Maximum experience is required")
    .matches(/^\d+$/, "Please select a valid number")
    .test(
      "gte-min",
      "Maximum experience must be greater than or equal to minimum experience",
      function (val) {
        const min = this.parent.min_job_exp
        if (min === undefined || val === undefined) return false
        if (min === "0" && val === "0") return true
        return Number(val) >= Number(min)
      }
    ),
  job_location: yup.array().of(yup.string()).min(1, "At least one job location is required"),
  job_skills: yup.array().of(yup.string()).min(1, "At least one skill is required"),
  job_education: yup.array().of(yup.string()).min(1, "At least one education requirement is required"),
})

function JobDetails2() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const jobDetails_1 = useLocation().state

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    trigger,
    watch,
  } = useForm({
    mode: "all",
    resolver: yupResolver(JobDetails2Schema),
    defaultValues: {
      upload_cv: true,
      job_apply_age: "18-25",
      min_job_exp: "0",
      max_job_exp: "3",
      candidate_contact_via: "By call",
      interview_type: "In-Person",
      job_location: [],
      job_skills: [],
      job_education: [],
    },
  })

  const watchMinExp = watch("min_job_exp")
  const watchMaxExp = watch("max_job_exp")

  const coreEducations = useSelector(selectCoreEducations)
  const coreLocation = useSelector(selectPublicCoreLocations)
  const coreLocationLoading = useSelector((state) => state.publicCoreLocations.loading)
  const coreSkills = useSelector(selectCoreSkills)
  const suggestedSkills = useSelector(selectSelectedSkills)
  const createJobStatus = useSelector((state) => state.createJobReducers.status)

  // Selected items (final chosen lists)
  const [location, setLocation] = useState([])
  const [locationId, setLocationId] = useState([])
  const [skills, setSkills] = useState([])
  const [skillsId, setSkillsId] = useState([])
  const [education, setEducation] = useState([])
  const [educationId, setEducationId] = useState([])

  // Searches
  const [skillSearch, setSkillSearch] = useState("")
  const [locationSearch, setLocationSearch] = useState("")

  // Menus open/close control
  const locationMenu = useDisclosure()
  const skillsMenu = useDisclosure()

  // Hierarchical education picker (Level -> Course -> Speciality)
  const [selectedEduLevel, setSelectedEduLevel] = useState(null)
  const [selectedEduCourse, setSelectedEduCourse] = useState(null)
  const [selectedEduSpeciality, setSelectedEduSpeciality] = useState(null)

  const levels = useMemo(() => (Array.isArray(coreEducations) ? coreEducations : []), [coreEducations])
  const courses = useMemo(
    () => (selectedEduLevel?.core_job_education_course || []),
    [selectedEduLevel]
  )
  const specialities = useMemo(
    () => (selectedEduCourse?.core_job_education_course_specialty || []),
    [selectedEduCourse]
  )

  const resetCourseAndBelow = () => {
    setSelectedEduCourse(null)
    setSelectedEduSpeciality(null)
  }
  const resetSpeciality = () => {
    setSelectedEduSpeciality(null)
  }
  const buildEduLabel = (level, course, spec) =>
    [level?.title, course?.title, spec?.title].filter(Boolean).join(" / ")

  const addSelectedEducation = () => {
    if (!selectedEduLevel) return
    const finalObj = selectedEduSpeciality || selectedEduCourse || selectedEduLevel
    const finalUuid = finalObj.uuid
    const label = buildEduLabel(selectedEduLevel, selectedEduCourse, selectedEduSpeciality)

    // prevent duplicates by UUID
    if (educationId.includes(finalUuid)) {
      return
    }

    const newItem = { id: finalUuid, uuid: finalUuid, title: label }
    const updatedEducation = [...education, newItem]
    const updatedEducationIds = [...educationId, finalUuid]

    setEducation(updatedEducation)
    setEducationId(updatedEducationIds)
    setValue("job_education", updatedEducationIds, { shouldValidate: true })
    trigger("job_education")

    // reset to start again from Level → Course → Speciality
    setSelectedEduLevel(null)
    resetCourseAndBelow()
  }

  useEffect(() => {
    dispatch(fetchPublicCoreLocationsAsync())
    dispatch(fetchCoreEducationAsync())
    dispatch(fetchCoreSkillsAsync())
    window.scrollTo(0, 0)
  }, [dispatch])

  useEffect(() => {
    if (watchMinExp && watchMaxExp) {
      trigger("max_job_exp")
    }
  }, [watchMinExp, watchMaxExp, trigger])

  // Sync arrays (IDs) to RHF for Yup validation
  useEffect(() => {
    setValue("job_location", locationId, { shouldValidate: true })
  }, [locationId, setValue])
  useEffect(() => {
    setValue("job_skills", skillsId, { shouldValidate: true })
  }, [skillsId, setValue])
  useEffect(() => {
    setValue("job_education", educationId, { shouldValidate: true })
  }, [educationId, setValue])

  // Select handlers (Location)
  const handleSelectLocationItem = (item) => {
    if (!item) return
    const alreadySelected = location.some((loc) => getId(loc) === getId(item))
    if (alreadySelected) return

    const updatedLocation = [...location, item]
    const updatedLocationId = [...locationId, getId(item)]

    setLocation(updatedLocation)
    setLocationId(updatedLocationId)
    setValue("job_location", updatedLocationId, { shouldValidate: true })

    // Close and clear search
    locationMenu.onClose()
    setLocationSearch("")
  }

  const removeLocation = (index) => {
    const updatedLocation = location.filter((_, i) => i !== index)
    const updatedLocationId = updatedLocation.map((loc) => getId(loc))

    setLocation(updatedLocation)
    setLocationId(updatedLocationId)
    setValue("job_location", updatedLocationId, { shouldValidate: true })
    trigger("job_location")
  }

  // Select handlers (Skills)
  const handleSelectSkillItem = (item) => {
    if (!item) return
    const alreadySelected = skills.some((s) => getId(s) === getId(item))
    if (alreadySelected) return

    const updateSkill = [...skills, item]
    const updateSkillId = [...skillsId, getId(item)]

    setSkills(updateSkill)
    setSkillsId(updateSkillId)
    setValue("job_skills", updateSkillId, { shouldValidate: true })

    // Close and clear search
    skillsMenu.onClose()
    setSkillSearch("")
  }

  const removeSkill = (index) => {
    const updatedSkills = skills.filter((_, i) => i !== index)
    const updatedSkillIds = updatedSkills.map((skill) => getId(skill))

    setSkills(updatedSkills)
    setSkillsId(updatedSkillIds)
    setValue("job_skills", updatedSkillIds, { shouldValidate: true })
    trigger("job_skills")
  }

  const removeEducation = (index) => {
    const updatedEducation = education.filter((_, i) => i !== index)
    const updatedEducationIds = updatedEducation.map((edu) => getId(edu))

    setEducation(updatedEducation)
    setEducationId(updatedEducationIds)
    setValue("job_education", updatedEducationIds, { shouldValidate: true })
    trigger("job_education")
  }

  // Debounced skill search (server)
  useEffect(() => {
    const handler = setTimeout(() => {
      const q = (skillSearch || "").trim()
      if (q.length > 0) {
        dispatch(fetchSuggestedSkillsAsync(q))
      }
    }, 500)
    return () => clearTimeout(handler)
  }, [skillSearch, dispatch])

  // Debounced location search (server)
  useEffect(() => {
    const handler = setTimeout(() => {
      const q = (locationSearch || "").trim()
      if (q.length > 0) {
        dispatch(fetchPublicCoreLocationsAsync({ keyword: q }))
      } else {
        dispatch(fetchPublicCoreLocationsAsync())
      }
    }, 500)
    return () => clearTimeout(handler)
  }, [locationSearch, dispatch])

  // Restore data when navigating back
  useEffect(() => {
    if (jobDetails_1) {
      setValue("upload_cv", jobDetails_1.upload_cv ?? true)
      setValue("job_apply_age", jobDetails_1.job_apply_age || "18-25")
      setValue("min_job_exp", jobDetails_1.min_job_exp || "0")
      setValue("max_job_exp", jobDetails_1.max_job_exp || "3")
      setValue("candidate_contact_via", jobDetails_1.candidate_contact_via || "By call")
      setValue("interview_type", jobDetails_1.interview_type || "In-Person")
      setValue("job_deadline", jobDetails_1.job_deadline || "")
      setValue("candidate_gender", jobDetails_1.candidate_gender || "Both")

      if (Array.isArray(jobDetails_1.job_location) && jobDetails_1.job_location.length > 0) {
        setLocation(jobDetails_1.job_location)
        setLocationId(jobDetails_1.job_location.map((loc) => getId(loc)))
      }
      if (Array.isArray(jobDetails_1.job_skills) && jobDetails_1.job_skills.length > 0) {
        setSkills(jobDetails_1.job_skills)
        setSkillsId(jobDetails_1.job_skills.map((skill) => getId(skill)))
      }
      if (Array.isArray(jobDetails_1.job_education) && jobDetails_1.job_education.length > 0) {
        setEducation(jobDetails_1.job_education)
        setEducationId(jobDetails_1.job_education.map((edu) => getId(edu)))
      }

      setTimeout(() => trigger(), 0)
    }
  }, [jobDetails_1, setValue, trigger])

  const onSubmit = (data) => {
    if (!data) return

    const newData = {
      // carry over page-1 fields (including new pay fields)
      additional_pay: jobDetails_1.additional_pay,
      address1: jobDetails_1.address1,
      address2: jobDetails_1.address2,
      city_uuid: jobDetails_1.city_uuid,
      country_uuid: jobDetails_1.country_uuid,
      department_uuid: jobDetails_1.department_uuid,
      hiring_company_name: jobDetails_1.hiring_company_name,
      job_name: jobDetails_1.job_name,
      is_planned_start_date: jobDetails_1.is_planned_start_date,
      job_description: jobDetails_1.job_description,
      job_shift: jobDetails_1.job_shift,
      job_tag: jobDetails_1.job_tag,
      job_title_uuid: jobDetails_1.job_title_uuid,
      job_type: jobDetails_1.job_type,
      joining_amount_required: jobDetails_1.joining_amount_required,
      joining_amount: jobDetails_1.joining_amount,
      no_of_hire: jobDetails_1.no_of_hire,
      pincode: jobDetails_1.pincode,
      plan_start_date: jobDetails_1.plan_start_date,
      salary_exact_amount: jobDetails_1.salary_exact_amount || 0,
      salary_max_amount: jobDetails_1.salary_max_amount,
      salary_min_amount: jobDetails_1.salary_min_amount,
      salary_per_type: jobDetails_1.salary_per_type,
      state_uuid: jobDetails_1.state_uuid,
      work_location_type: jobDetails_1.work_location_type,
      pay_type: jobDetails_1.pay_type,
      salary_not_disclose: jobDetails_1.salary_not_disclose,

      // page-2 fields
      candidate_contact_via: data.candidate_contact_via,
      job_apply_age: data.job_apply_age,
      interview_type: data.interview_type,
      job_deadline: data.job_deadline,
      max_job_exp: data.max_job_exp,
      candidate_gender: data.candidate_gender,

      job_location: location.map((loc) => ({
        id: getId(loc),
        title: getTitle(loc),
      })),
      job_education: education.map((edu) => ({
        id: getId(edu),
        title: getTitle(edu),
      })),
      job_skills: skills.map((skill) => ({
        id: getId(skill),
        title: getTitle(skill),
      })),

      min_job_exp: data.min_job_exp,
      upload_cv: data.upload_cv || false,
      is_published: data.is_published || false,
    }
    navigate("/employer-dashboard/post-job/review", { state: newData })
  }

  const onSubmitError = (formErrors) => {
    const firstKey = Object.keys(formErrors)[0]
    if (!firstKey) return
    const fieldEl =
      document.querySelector(`[name="${firstKey}"]`) ||
      document.getElementById(`${firstKey}-section`) ||
      document.getElementById("job-location-section")
    if (fieldEl?.scrollIntoView) {
      fieldEl.scrollIntoView({ behavior: "smooth", block: "center" })
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const today = new Date().toISOString().split("T")[0]

  const skillsListToShow = useMemo(() => {
    const q = (skillSearch || "").trim()
    if (q.length > 0 && Array.isArray(suggestedSkills)) {
      return suggestedSkills
    }
    return Array.isArray(coreSkills) ? coreSkills : []
  }, [skillSearch, suggestedSkills, coreSkills])

  const isSuggestLoading = createJobStatus === "loading" && (skillSearch || "").trim().length > 0

  return (
    <Box minH="100vh" py={12} className=" bg-mainBg">
      <Container maxW="6xl">
        <Box bg="white" borderRadius="xl" className="shadow-md" p={6} transition="all 0.3s ease">
          <Heading as="h2" size="xl" mb={2} textAlign="center" color="blue.600">
            Additional Job Details
          </Heading>
          <Text textAlign="center" color="gray.500" mb={8}>
            Fill in the requirements and qualifications for this position
          </Text>

          <form onSubmit={handleSubmit(onSubmit, onSubmitError)} noValidate>
            <VStack spacing={2} align="stretch">
              <Box p={5} borderWidth="1px" borderRadius="lg" borderColor="blue.100" bg="blue.50">
                <FormControl isInvalid={!!errors.upload_cv}>
                  <FormLabel display="flex" alignItems="center">
                    Upload CV?
                    <Tooltip label="Choose whether applicants must upload their CV">
                      <Box as="span" ml={1}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                      </Box>
                    </Tooltip>
                  </FormLabel>
                  <RadioGroup
                    onChange={(value) => {
                      const mustUpload = value === "true"
                      setValue("upload_cv", mustUpload, { shouldValidate: true })
                    }}
                    defaultValue="true"
                  >
                    <Stack direction="row">
                      <Radio value="true">Yes</Radio>
                      <Radio value="false">No</Radio>
                    </Stack>
                  </RadioGroup>
                  <FormErrorMessage color="red.500">{errors.upload_cv && errors.upload_cv.message}</FormErrorMessage>
                </FormControl>
              </Box>

              <Divider />

              <Box>
                <Heading as="h3" size="md" mb={2} color="blue.700">
                  Location and Demographic Requirements
                </Heading>

                {/* Job Posting Location */}
                <FormControl isInvalid={!!errors.job_location} mb={6} id="job-location-section">
                  <FormLabel>Job Posting Location</FormLabel>

                  <Menu
                    isOpen={locationMenu.isOpen}
                    onOpen={locationMenu.onOpen}
                    onClose={() => {
                      locationMenu.onClose()
                      setLocationSearch("")
                    }}
                    closeOnSelect
                    autoSelect={false}
                  >
                    <MenuButton
                      as={Button}
                      variant="outline"
                      rightIcon={<ChevronDownIcon className="h-4 w-4" />}
                      width="100%"
                      justifyContent="space-between"
                      fontWeight="normal"
                      borderColor={errors.job_location ? "red.500" : "gray.200"}
                      className="text-left"
                    >
                      Add job locations
                    </MenuButton>
                    <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                      <Input
                        size="sm"
                        placeholder="Type to search locations..."
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault()
                        }}
                        autoFocus
                      />
                      <Box maxH="260px" overflowY="auto" mt={2}>
                        {coreLocationLoading ? (
                          <HStack p={3}>
                            <Spinner size="sm" />
                            <Text>Searching...</Text>
                          </HStack>
                        ) : coreLocation && coreLocation.length > 0 ? (
                          coreLocation.map((item) => {
                            const alreadySelected = location.some((loc) => getId(loc) === getId(item))
                            return (
                              <MenuItem
                                key={getId(item)}
                                onClick={() => handleSelectLocationItem(item)}
                                isDisabled={alreadySelected}
                              >
                                <HStack w="100%" className="items-start">
                                  <Text>{item.title}</Text>
                                  {alreadySelected && (
                                    <Text ml="auto" fontSize="xs" color="blue.500">
                                      Added
                                    </Text>
                                  )}
                                </HStack>
                              </MenuItem>
                            )
                          })
                        ) : (
                          <Text p={3} color="gray.500">
                            No results
                          </Text>
                        )}
                      </Box>
                    </MenuList>
                  </Menu>

                  {location.length > 0 ? (
                    <HStack mt={2} flexWrap="wrap" spacing={2}>
                      {location.map((loc, index) => (
                        <Tag key={getId(loc) || index} size="md" borderRadius="full" variant="solid" colorScheme="blue" mt={1}>
                          <TagLabel>{getTitle(loc)}</TagLabel>
                          <TagCloseButton onClick={() => removeLocation(index)} />
                        </Tag>
                      ))}
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Add one or more job locations
                    </Text>
                  )}
                  <FormErrorMessage color="red.500">
                    {errors.job_location && errors.job_location.message}
                  </FormErrorMessage>
                </FormControl>

                {/* Demographic fields */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isInvalid={!!errors.job_apply_age} isRequired>
                    <FormLabel>Age Range</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Select
                        {...register("job_apply_age")}
                        defaultValue="18-25"
                        pl={10}
                        bg="white"
                        cursor="pointer"
                      >
                        <option value="18-25">18-25 years</option>
                        <option value="26-30">26-30 years</option>
                        <option value="31-35">31-35 years</option>
                        <option value="36-40">36-40 years</option>
                        <option value="45+">45+ years</option>
                      </Select>
                    </InputGroup>
                    <FormErrorMessage color="red.500">
                      {errors.job_apply_age && errors.job_apply_age.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.candidate_gender} isRequired>
                    <FormLabel>Gender</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Select
                        {...register("candidate_gender")}
                        defaultValue="Both"
                        pl={10}
                        bg="white"
                        cursor="pointer"
                      >
                        <option value="Both">Both</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </Select>
                    </InputGroup>
                    <FormErrorMessage color="red.500">
                      {errors.candidate_gender && errors.candidate_gender.message}
                    </FormErrorMessage>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              <Box>
                <Heading as="h3" size="md" mb={2} color="blue.700">
                  Skills and Qualifications
                </Heading>
                <VStack spacing={6} align="stretch">
                  <FormControl isInvalid={!!errors.job_skills}>
                    <FormLabel>Skills Required</FormLabel>

                    <Menu
                      isOpen={skillsMenu.isOpen}
                      onOpen={skillsMenu.onOpen}
                      onClose={() => {
                        skillsMenu.onClose()
                        setSkillSearch("")
                      }}
                      closeOnSelect
                      autoSelect={false}
                    >
                      <MenuButton
                        as={Button}
                        variant="outline"
                        rightIcon={<ChevronDownIcon className="h-4 w-4" />}
                        width="100%"
                        justifyContent="space-between"
                        fontWeight="normal"
                        borderColor={errors.job_skills ? "red.500" : "gray.200"}
                        className="text-left"
                      >
                        Add required skills
                      </MenuButton>
                      <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                        <Input
                          size="sm"
                          placeholder="Type to search skills..."
                          value={skillSearch}
                          onChange={(e) => setSkillSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.preventDefault()
                          }}
                          autoFocus
                        />
                        <Box maxH="260px" overflowY="auto" mt={2}>
                          {isSuggestLoading ? (
                            <HStack p={3}>
                              <Spinner size="sm" />
                              <Text>Searching...</Text>
                            </HStack>
                          ) : skillsListToShow && skillsListToShow.length > 0 ? (
                            skillsListToShow.map((item) => {
                              const alreadySelected = skills.some((s) => getId(s) === getId(item))
                              return (
                                <MenuItem
                                  key={getId(item)}
                                  onClick={() => handleSelectSkillItem(item)}
                                  isDisabled={alreadySelected}
                                >
                                  {item.title}
                                  {alreadySelected && (
                                    <Text ml="auto" fontSize="xs" color="blue.500">
                                      Added
                                    </Text>
                                  )}
                                </MenuItem>
                              )
                            })
                          ) : (
                            <Text p={3} color="gray.500">
                              No results
                            </Text>
                          )}
                        </Box>
                      </MenuList>
                    </Menu>

                    {skills.length > 0 ? (
                      <HStack mt={2} flexWrap="wrap" spacing={2}>
                        {skills.map((skill, index) => (
                          <Tag
                            key={getId(skill) || index}
                            size="md"
                            borderRadius="full"
                            variant="solid"
                            colorScheme="green"
                            mt={1}
                          >
                            <TagLabel>{getTitle(skill)}</TagLabel>
                            <TagCloseButton onClick={() => removeSkill(index)} />
                          </Tag>
                        ))}
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        Add one or more required skills
                      </Text>
                    )}
                    <FormErrorMessage color="red.500">
                      {errors.job_skills && errors.job_skills.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Education Requirements (Hierarchical: Level → Course → Speciality) */}
                  <FormControl isInvalid={!!errors.job_education}>
                    <FormLabel>Education Requirements</FormLabel>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                      {/* Education Level (required) */}
                      <Select
                        placeholder="Select education level"
                        value={selectedEduLevel?.uuid ?? ""}
                        onChange={(e) => {
                          const lvl = levels.find((l) => l.uuid === e.target.value)
                          setSelectedEduLevel(lvl || null)
                          resetCourseAndBelow()
                        }}
                        bg="white"
                        cursor="pointer"
                      >
                        {levels.map((lvl) => (
                          <option key={lvl.uuid} value={lvl.uuid}>
                            {lvl.title}
                          </option>
                        ))}
                      </Select>

                      {/* Course (optional) */}
                      <Select
                        placeholder={courses.length ? "Select course (optional)" : "No courses"}
                        value={selectedEduCourse?.uuid ?? ""}
                        onChange={(e) => {
                          const crs = courses.find((c) => c.uuid === e.target.value)
                          setSelectedEduCourse(crs || null)
                          resetSpeciality()
                        }}
                        isDisabled={!selectedEduLevel || courses.length === 0}
                        bg="white"
                        cursor={courses.length ? "pointer" : "not-allowed"}
                      >
                        {courses.map((crs) => (
                          <option key={crs.uuid} value={crs.uuid}>
                            {crs.title}
                          </option>
                        ))}
                      </Select>

                      {/* Speciality (optional) */}
                      <Select
                        placeholder={specialities.length ? "Select speciality (optional)" : "No specialities"}
                        value={selectedEduSpeciality?.uuid ?? ""}
                        onChange={(e) => {
                          const spec = specialities.find((s) => s.uuid === e.target.value)
                          setSelectedEduSpeciality(spec || null)
                        }}
                        isDisabled={!selectedEduCourse || specialities.length === 0}
                        bg="white"
                        cursor={specialities.length ? "pointer" : "not-allowed"}
                      >
                        {specialities.map((spec) => (
                          <option key={spec.uuid} value={spec.uuid}>
                            {spec.title}
                          </option>
                        ))}
                      </Select>
                    </SimpleGrid>

                    <HStack mt={3} spacing={3}>
                      <Button colorScheme="blue" onClick={addSelectedEducation} isDisabled={!selectedEduLevel}>
                        Add
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setSelectedEduLevel(null)
                          resetCourseAndBelow()
                        }}
                      >
                        Clear selection
                      </Button>
                    </HStack>

                    {/* Selected items as tags */}
                    {education.length > 0 ? (
                      <HStack mt={3} flexWrap="wrap" spacing={2}>
                        {education.map((edu, index) => (
                          <Tag
                            key={getId(edu) || index}
                            size="md"
                            borderRadius="full"
                            variant="solid"
                            colorScheme="purple"
                            mt={1}
                          >
                            <TagLabel>{getTitle(edu)}</TagLabel>
                            <TagCloseButton onClick={() => removeEducation(index)} />
                          </Tag>
                        ))}
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.500" mt={2}>
                        Add at least one education requirement
                      </Text>
                    )}

                    <FormErrorMessage color="red.500">
                      {errors.job_education && errors.job_education.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Experience Required</FormLabel>
                    <Select
                      onChange={(e) => {
                        const isFresher = e.target.value === "fresher"
                        if (isFresher) {
                          setValue("min_job_exp", "0", { shouldValidate: true })
                          setValue("max_job_exp", "0", { shouldValidate: true })
                        } else {
                          if (watch("min_job_exp") === "0" && watch("max_job_exp") === "0") {
                            setValue("min_job_exp", "1", { shouldValidate: true })
                            setValue("max_job_exp", "3", { shouldValidate: true })
                          }
                        }
                        trigger(["min_job_exp", "max_job_exp"])
                      }}
                      mb={4}
                      bg="white"
                      cursor="pointer"
                      defaultValue="experienced"
                    >
                      <option value="fresher">Fresher</option>
                      <option value="experienced">Experienced</option>
                    </Select>

                    {watch("min_job_exp") === "0" && watch("max_job_exp") === "0" ? (
                      <Text fontSize="sm" color="blue.500" mb={2}>
                        No experience required (Fresher position)
                      </Text>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl isInvalid={!!errors.min_job_exp} isRequired>
                          <FormLabel fontSize="sm">Minimum Experience</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                            </InputLeftElement>
                            <Select
                              {...register("min_job_exp")}
                              pl={10}
                              defaultValue="1"
                              bg="white"
                              cursor="pointer"
                            >
                              <option value="0">0 year</option>
                              <option value="1">1 year</option>
                              <option value="2">2 years</option>
                              <option value="3">3 years</option>
                              <option value="4">4 years</option>
                              <option value="5">5 years</option>
                            </Select>
                          </InputGroup>
                          <FormErrorMessage color="red.500">
                            {errors.min_job_exp && errors.min_job_exp.message}
                          </FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={!!errors.max_job_exp} isRequired>
                          <FormLabel fontSize="sm">Maximum Experience</FormLabel>
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                            </InputLeftElement>
                            <Select
                              {...register("max_job_exp")}
                              pl={10}
                              defaultValue="3"
                              bg="white"
                              cursor="pointer"
                            >
                              <option value="1">1 year</option>
                              <option value="2">2 years</option>
                              <option value="3">3 years</option>
                              <option value="4">4 years</option>
                              <option value="5">5 years</option>
                              <option value="6">6 years</option>
                              <option value="7">7 years</option>
                              <option value="8">8 years</option>
                              <option value="9">9 years</option>
                              <option value="10">10 years</option>
                              <option value="11">11+ years</option>
                            </Select>
                          </InputGroup>
                          <FormErrorMessage color="red.500">
                            {errors.max_job_exp && errors.max_job_exp.message}
                          </FormErrorMessage>
                        </FormControl>
                      </SimpleGrid>
                    )}
                  </FormControl>
                </VStack>
              </Box>

              <Divider />

              <Box>
                <Heading as="h3" size="md" mb={2} color="blue.700">
                  Application Process
                </Heading>
                <VStack spacing={6} align="stretch">
                  <FormControl isRequired isInvalid={!!errors.interview_type}>
                    <FormLabel>Interview Type</FormLabel>
                    <Controller
                      name="interview_type"
                      control={control}
                      defaultValue="In-Person"
                      rules={{ required: "Interview type is required" }}
                      render={({ field }) => (
                        <RadioGroup {...field}>
                          <Stack direction="column" spacing={1} alignItems={"start"}>
                            <Radio value="In-Person" alignItems={"start"}>
                              <HStack alignItems="center">
                                <Text>In-Person / Walk-in Interview</Text>
                                <Tag size="sm" colorScheme="orange" ml={2}>
                                  Face-to-face
                                </Tag>
                              </HStack>
                            </Radio>
                            <Radio value="Online" alignItems={"start"}>
                              <HStack alignItems="start">
                                <Text>Telephonic / Online Interview</Text>
                                <Tag size="sm" colorScheme="blue" ml={2}>
                                  Remote
                                </Tag>
                              </HStack>
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      )}
                    />
                    <FormErrorMessage color="red.500">
                      {errors.interview_type && errors.interview_type.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.candidate_contact_via}>
                    <FormLabel>Candidate Contact Preference</FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Select
                        {...register("candidate_contact_via")}
                        pl={10}
                        defaultValue="By call"
                        bg="white"
                        cursor="pointer"
                      >
                        <option value="By call">By call</option>
                        <option value="By email">By email</option>
                        <option value="By WhatsApp">By WhatsApp</option>
                        <option value="All modes">All modes</option>
                      </Select>
                    </InputGroup>
                    <FormErrorMessage color="red.500">
                      {errors.candidate_contact_via && errors.candidate_contact_via.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.job_deadline}>
                    <FormLabel display="flex" alignItems="center">
                      Last Date to apply
                      <Tooltip label="Last date when candidates can apply for this position">
                        <Box as="span" ml={1}>
                          <InformationCircleIcon className="h-4 w-4 text-gray-500" />
                        </Box>
                      </Tooltip>
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <CalendarIcon className="h-5 w-5 text-gray-400" />
                      </InputLeftElement>
                      <Input
                        type="date"
                        {...register("job_deadline")}
                        pl={10}
                        min={today}
                        bg="white"
                      />
                    </InputGroup>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Job will be automatically closed after this date
                    </Text>
                    <FormErrorMessage color="red.500">
                      {errors.job_deadline && errors.job_deadline.message}
                    </FormErrorMessage>
                  </FormControl>
                </VStack>
              </Box>

              <Divider my={2} />

              <HStack justifyContent="space-between" pt={4}>
                <Button
                  leftIcon={<ChevronLeftIcon className="h-5 w-5" />}
                  colorScheme="blue"
                  variant="outline"
                  size="lg"
                  _hover={{ bg: "blue.50" }}
                  onClick={() => navigate("/employer-dashboard/post-job", { state: { formData: jobDetails_1 } })}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  rightIcon={<ChevronRightIcon className="h-5 w-5" />}
                  colorScheme="blue"
                  size="lg"
                  px={8}
                  _hover={{ bg: "blue.600" }}
                >
                  Continue to Review
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Container>
    </Box>
  )
}

export default JobDetails2